import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBlogPosts, getBlogCategoryBySlug } from '../../../services/blogService';
import { BlogPost, BlogCategory } from '../../../types/blog';

/**
 * Blog category page component
 * Displays blog posts filtered by category
 * 
 * @returns {JSX.Element} The rendered Blog category page
 */
const BlogCategoryPage: React.FC = () => {
  // Get slug from URL params
  const { slug } = useParams<{ slug: string }>();
  
  // State for blog posts and category
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [category, setCategory] = useState<BlogCategory | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Load blog data
  useEffect(() => {
    try {
      // Log component rendering
      console.log('Rendering Blog category page for slug:', slug);
      
      if (!slug) {
        setError('Category not found');
        setIsLoading(false);
        return;
      }
      
      // Fetch category
      const fetchedCategory = getBlogCategoryBySlug(slug);
      
      if (!fetchedCategory) {
        setError('Category not found');
        setIsLoading(false);
        return;
      }
      
      setCategory(fetchedCategory);
      
      // Fetch blog posts for this category
      const fetchedPosts = getBlogPosts(slug);
      setPosts(fetchedPosts);
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading blog category:', error);
      setError('An error occurred while loading the blog category');
      setIsLoading(false);
    }
  }, [slug]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="relative overflow-hidden min-h-screen">
        <div className="flex justify-center items-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error || !category) {
    return (
      <div className="relative overflow-hidden min-h-screen">
        <div className="mx-auto max-w-7xl px-6 py-24 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{error || 'Category not found'}</h1>
          <p className="text-gray-600 mb-8">The blog category you're looking for doesn't exist or has been moved.</p>
          <Link 
            to="/blog" 
            className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden">
      {/* Decorative elements matching Hero section */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-indigo-200 blur-3xl"></div>
        <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-purple-200 blur-3xl"></div>
        <div className="absolute -bottom-24 left-1/4 w-72 h-72 rounded-full bg-pink-200 blur-3xl"></div>
      </div>
      
      {/* Subtle dot pattern overlay */}
      <div 
        className="absolute inset-0 opacity-10" 
        style={{
          backgroundImage: 'radial-gradient(#4f46e5 0.5px, transparent 0.5px)',
          backgroundSize: '12px 12px'
        }}
      ></div>
      
      {/* Hero section */}
      <div className="relative">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-8">
          {/* Back to blog link */}
          <Link 
            to="/blog" 
            className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500 mb-6"
          >
            <svg className="mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
            </svg>
            Back to Blog
          </Link>
          
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            {category.name}
          </h1>
          <p className="mt-4 text-lg leading-8 text-gray-600 max-w-3xl">
            {category.description}
          </p>
        </div>
      </div>

      {/* Blog posts */}
      <div className="relative mx-auto max-w-7xl px-6 pb-16 lg:px-8">
        {posts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <article key={post.id} className="group relative bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden transition-all duration-200 hover:shadow-lg">
                <Link to={`/blog/${post.slug}`} className="block">
                  <div className="aspect-[16/9] w-full bg-gray-100 overflow-hidden">
                    <img 
                      src={'/blog-posters/'+post.slug+'.png'} 
                      alt={post.title} 
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-x-4 text-xs mb-2">
                      <time dateTime={post.date} className="text-gray-500">{formatDate(post.date)}</time>
                      <span className="text-gray-500">{post.readTime} min read</span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 group-hover:text-indigo-600 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="mt-2 text-gray-600 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="mt-4 flex items-center gap-x-4">
                      <div className="flex items-center gap-x-2">
                        <img src={post.author.avatar} alt={post.author.name} className="h-8 w-8 rounded-full bg-gray-100" />
                        <span className="text-sm font-medium text-gray-900">{post.author.name}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900">No posts found in this category</h3>
            <p className="mt-2 text-gray-600">Check back later for new content.</p>
            <Link 
              to="/blog" 
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              View all posts
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogCategoryPage;
